import { z } from 'zod';
import { zodResolver } from '@hookform/resolvers/zod';
import { Loader2Icon } from 'lucide-react';
import { useForm } from 'react-hook-form';
import { Link, useNavigate } from 'react-router-dom';
import { passwordSchema, registerSchema } from '@shared/schemas/auth';
import { authClient } from '@/lib/auth-client';
import { getAuthErrorMessage } from '@/lib/auth-errors';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import {
  Field,
  FieldDescription,
  FieldError,
  FieldGroup,
  FieldLabel,
  FieldSeparator,
} from '@/components/ui/field';
import { Input } from '@/components/ui/input';
import { toast } from 'sonner';

const signupFormSchema = registerSchema.extend({
  confirmPassword: passwordSchema,
}).refine((values) => values.password === values.confirmPassword, {
  error: 'Les mots de passe doivent être identiques.',
  path: ['confirmPassword'],
});

type SignupFormValues = z.infer<typeof signupFormSchema>;

export function SignupForm({
  className,
  ...props
}: React.ComponentProps<'form'>) {
  const navigate = useNavigate();
  const form = useForm<SignupFormValues>({
    defaultValues: {
      confirmPassword: '',
      email: '',
      name: '',
      password: '',
    },
    resolver: zodResolver(signupFormSchema),
  });

  async function handleRegister(values: SignupFormValues) {
    form.clearErrors('root');

    const result = await authClient.signUp.email({
      email: values.email,
      name: values.name,
      password: values.password,
    });

    if (result.error) {
      const message = getAuthErrorMessage(result.error);
      form.setError('root', { message });
      toast.error(message);
      return;
    }

    toast.success('Compte créé. Vérifiez maintenant votre adresse e-mail.');
    navigate(`/auth/verify-email?email=${encodeURIComponent(values.email)}`, {
      replace: true,
    });
  }

  return (
    <form className={cn('flex flex-col gap-6', className)} onSubmit={form.handleSubmit(handleRegister)} {...props}>
      <FieldGroup>
        <div className="flex flex-col items-center gap-1 text-center">
          <h1 className="text-2xl font-bold">Créez votre compte</h1>
          <p className="text-sm text-balance text-muted-foreground">
            L inscription déclenche un email de confirmation avant la première connexion.
          </p>
        </div>
        <Field>
          <FieldLabel htmlFor="name">Nom complet</FieldLabel>
          <Input
            id="name"
            data-testid="signup-name"
            type="text"
            placeholder="Anne Onyme"
            autoComplete="name"
            className="bg-background"
            aria-invalid={form.formState.errors.name ? 'true' : 'false'}
            {...form.register('name')}
          />
          <FieldError errors={[form.formState.errors.name]} />
        </Field>
        <Field>
          <FieldLabel htmlFor="email">Email</FieldLabel>
          <Input
            id="email"
            data-testid="signup-email"
            type="email"
            placeholder="anne.onyme@example.com"
            autoComplete="email"
            className="bg-background"
            aria-invalid={form.formState.errors.email ? 'true' : 'false'}
            {...form.register('email')}
          />
          <FieldDescription>
            Cet email recevra le lien de confirmation et les futurs emails de sécurité.
          </FieldDescription>
          <FieldError errors={[form.formState.errors.email]} />
        </Field>
        <Field>
          <FieldLabel htmlFor="password">Mot de passe</FieldLabel>
          <Input
            id="password"
            data-testid="signup-password"
            type="password"
            autoComplete="new-password"
            className="bg-background"
            aria-invalid={form.formState.errors.password ? 'true' : 'false'}
            {...form.register('password')}
          />
          <FieldDescription>Au moins 8 caractères.</FieldDescription>
          <FieldError errors={[form.formState.errors.password]} />
        </Field>
        <Field>
          <FieldLabel htmlFor="confirm-password">Confirmation du mot de passe</FieldLabel>
          <Input
            id="confirm-password"
            data-testid="signup-confirm-password"
            type="password"
            autoComplete="new-password"
            className="bg-background"
            aria-invalid={form.formState.errors.confirmPassword ? 'true' : 'false'}
            {...form.register('confirmPassword')}
          />
          <FieldError errors={[form.formState.errors.confirmPassword]} />
        </Field>
        <FieldError errors={[form.formState.errors.root]} />
        <Field>
          <Button data-testid="signup-submit" type="submit" disabled={form.formState.isSubmitting}>
            {form.formState.isSubmitting ? <Loader2Icon className="animate-spin" /> : null}
            Créer mon compte
          </Button>
        </Field>
        <FieldSeparator>Déjà inscrit ?</FieldSeparator>
        <Field>
          <Button asChild variant="outline" type="button">
            <Link to="/auth/login">Retour à la connexion</Link>
          </Button>
          <FieldDescription className="px-6 text-center">
            Vous validerez ensuite votre adresse e-mail avant d’accéder à l’application.
          </FieldDescription>
        </Field>
      </FieldGroup>
    </form>
  );
}
